import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { usePreviewContext } from "../../../contexts/preview-context";
import { useAppContext } from "../../../hooks/useAppContext";
import { transformPoint } from "../../../transform";
import { useCommandManager } from "../../../hooks/useCommandManager";
import { useGuidelineOverlay } from "../../guideline-overlay/use-guideline-overlay";
import { ZoneSelectionBoxHandlers } from "../../zone-selection-box/zone-selection-box-handlers";
import { getAllowedZoneRegion, getDisallowedZoneRegion } from "./use-zone-move";
import { TransformType, useSnapping } from "./use-snapping";
/** Shared state */
export const zoneRotateState = proxy({
	isZoneRotating: false,
	actions: {
		setIsZoneRotating(isRotating: boolean) {
			zoneRotateState.isZoneRotating = isRotating;
		},
	},
});

/**
 * Snap to angles.
 */
function snapToAngles(angle: number, snapAngles = 45, snapDistance = 5): number {
	// Make positive angle to work
	angle = ((angle % 360) + 360) % 360;

	/// Example for multiples of 45°:
	// -1° at 44°, 0° at 45°, 1° at 46°, -1 at 89°
	const distanceFromSnapAngle = ((angle + snapAngles / 2) % snapAngles) - snapAngles / 2;

	if (Math.abs(distanceFromSnapAngle) < snapDistance) {
		angle -= distanceFromSnapAngle;
	}
	return angle;
}

/**
 * Logic to rotate a zone
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function useZoneRotate() {
	/** The start location of dragging (In DIP so it is independent of scale panning) */
	const zoneRotateRef = useRef<
		| {
				selectedZone: eXpress.core.ITransformableZone;

				/** Angle between zone center and pointer at start */
				startPointerAngleRad: number;

				/** The zone transform at the start */
				startZoneTransform: eXpress.core.IZoneTransform;

				/** Remember our command row Id to know if we can finalize/undo it at the end */
				undoRowId: string | undefined;

				/** The region were we can place out zone */
				allowedRegion: eXpress.math.Rect | undefined;

				/** The region were no zone can be placed */
				disallowedRegion: eXpress.math.Rect | undefined;

				/** Log once during drag */
				hasLoggedDisallowedRegionError?: boolean;
		  }
		| undefined
	>(undefined);

	const { getSpreadToScreenTransform } = usePreviewContext();

	const commandManager = useCommandManager();

	const { googleAnalytics, client } = useAppContext();
	const [lines, useLines] = useGuidelineOverlay();
	const snapTransform = useSnapping();

	const { isZoneRotating } = useSnapshot(zoneRotateState);

	const start = useCallback(
		function startZoneRotating({
			selectedZone,
			pointOnScreen,
		}: {
			selectedZone: eXpress.core.ITransformableZone;
			pointOnScreen: eXpress.math.Point;
		}) {
			// Get screen to spread
			const spreadToScreen = getSpreadToScreenTransform(selectedZone.spread, undefined);
			if (!spreadToScreen) return;

			// Get center of zone (the rectangle of a rotated zones changes as the origin is top-left instead of center)
			const startZoneTransform = selectedZone.transform();
			const zoneCenterDip = startZoneTransform.getCenter();
			const zoneCenterPx = transformPoint(zoneCenterDip, spreadToScreen); // Transform to screen space

			// Angle in radians between center of zone and pointer
			const startPointerAngleRad = Math.atan2(
				pointOnScreen.top - zoneCenterPx.top,
				pointOnScreen.left - zoneCenterPx.left,
			);

			zoneRotateState.actions.setIsZoneRotating(true);
			useLines.setIsZoneMoving(true);
			useLines.setTransformType(TransformType.Rotate);
			useLines.setHandle(ZoneSelectionBoxHandlers.None);

			zoneRotateRef.current = {
				selectedZone,
				startPointerAngleRad,
				startZoneTransform,
				undoRowId: undefined,
				allowedRegion: getAllowedZoneRegion(selectedZone),
				disallowedRegion: getDisallowedZoneRegion(selectedZone),
			};
		},
		[getSpreadToScreenTransform, useLines],
	);

	const update = useCallback(
		function zoneRotating(pointOnScreen: eXpress.math.Point) {
			const {
				startPointerAngleRad,
				startZoneTransform,
				selectedZone,
				allowedRegion,
				disallowedRegion,
				hasLoggedDisallowedRegionError,
			} = zoneRotateRef.current || {};

			if (
				!eXpress.utils.isNumber(startPointerAngleRad) ||
				!selectedZone ||
				!allowedRegion ||
				!startZoneTransform
			)
				return;

			// Get screen to spread
			const spreadToScreen = getSpreadToScreenTransform(selectedZone.spread, undefined);
			if (!spreadToScreen) return;

			// Get center of zone (the rectangle of a rotated zones changes as the origin is top-left instead of center)
			const zoneCenterDip = startZoneTransform.getCenter();
			const zoneCenterScreenPx = transformPoint(zoneCenterDip, spreadToScreen); // Transform to screen space

			// Angle in radians between center of zone and pointer
			const currentPointerAngleRad = Math.atan2(
				pointOnScreen.top - zoneCenterScreenPx.top,
				pointOnScreen.left - zoneCenterScreenPx.left,
			);

			const deltaAngleRad = currentPointerAngleRad - startPointerAngleRad;

			// Get new absolute zone angle
			let newAngleDeg =
				startZoneTransform.angle + eXpress.math.radiansToDegrees(deltaAngleRad);

			// Snap to multiples of 45°
			newAngleDeg = snapToAngles(newAngleDeg, 45, 5);

			// Quantize to 0.1 of a degree
			newAngleDeg = eXpress.math.quant(newAngleDeg, 10);

			// Make it fall inside range [0..360)
			newAngleDeg = eXpress.math.normalizeAnglePoint(newAngleDeg);

			const newParallelogram = eXpress.math
				// Place center of zone on origin
				.rect(-0.5, -0.5, 1, 1)
				.scaleByVector(startZoneTransform)
				.toFrame()
				// Rotate
				.rotateDeg(newAngleDeg)
				// Move back to original center
				.translate(startZoneTransform.getCenter());

			// Ensure we stay in allowed region:
			// Option 1: apply the rotate and correct by MOVING the zone back inside: use moveTowardAllowedRegion
			// Option 2: snap to the closest rotation allowed to rotate it back inside (not always possible if zone is far outside, fallback to move?)
			// Option 3: Ignore invalid values (at fast rotations it will not use the limits)
			// PO decided to use option 3
			if (!newParallelogram.overlaps(allowedRegion.toFrame())) {
				return;
			}

			// TODO: prevent moving to a 'disallowed' region
			// Use case: a multi-page product where one page is fixed
			// We could skip if the update if the new zone position is on a disallowed zone, but then we:
			// - cannot be moved from a disallowed zone to an allowed zone
			// - will not always be at the DIP limit of the maximum/minimum size
			if (
				!hasLoggedDisallowedRegionError &&
				disallowedRegion &&
				newParallelogram.overlaps(disallowedRegion.toFrame())
			) {
				const error = new eXpress.NotSupportedException(
					`Moving a zone on a multipage spread '${selectedZone.spread?.toString()}' where a page is fixed.`,
				);
				client.logErrorAsync(error, false);
				if (zoneRotateRef.current)
					zoneRotateRef.current.hasLoggedDisallowedRegionError = true;
			}

			// New zone transform to use
			const newZoneTransform = startZoneTransform.clone() as eXpress.core.ZoneTransform;
			newZoneTransform.setParallelogram(newParallelogram);

			if (snapTransform) {
				const transformed = snapTransform(newZoneTransform, lines, TransformType.Rotate);
				if (!transformed.isSimilarTo(newZoneTransform)) {
					newZoneTransform.left = transformed.left;
					newZoneTransform.top = transformed.top;
					newZoneTransform.width = transformed.width;
					newZoneTransform.height = transformed.height;
					newZoneTransform.angle = transformed.angle;
				}
			}

			const command = eXpress.core.commands.createTranslateZoneCommand(
				selectedZone,
				newZoneTransform,
				true,
			);

			// When dragging, we can receive events faster than the screen can show (faster than refresh rate of the screen)
			// I tested with RAF throttling but that caused a noticable delay when dragging, did not use RAF throttling
			commandManager.exec(command);
			if (zoneRotateRef.current) {
				zoneRotateRef.current.undoRowId = commandManager.getRowId();
			}
		},
		[getSpreadToScreenTransform, commandManager, client, snapTransform, lines],
	);

	const end = useCallback(
		function endZoneRotating() {
			if (zoneRotateRef.current?.undoRowId === commandManager.getRowId()) {
				commandManager.makeLastNonInteractive();
				googleAnalytics.trackEventToGa({
					trackingAction: "ZoneRotated",
				});
			}
			zoneRotateState.actions.setIsZoneRotating(false);
			useLines.setIsZoneMoving(false);
			useLines.setTransformType(TransformType.Move);
			zoneRotateRef.current = undefined;
		},
		[commandManager, googleAnalytics, useLines],
	);

	const cancel = useCallback(
		function cancelZoneResizing() {
			// Undo if last command is our resize
			if (zoneRotateRef.current?.undoRowId === commandManager.getRowId()) {
				commandManager.undo(true);
			}
			zoneRotateState.actions.setIsZoneRotating(false);
			useLines.setIsZoneMoving(false);
			useLines.setTransformType(TransformType.Move);
			zoneRotateRef.current = undefined;
		},
		[commandManager, useLines],
	);

	return [
		isZoneRotating,
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
